import React, { useEffect } from "react";
import { useParams, Navigate } from "react-router-dom";
import LocalSEO from "./LocalSEOTemplate/LocalSEOTemplate";
import blogDataDC from "@/core/Blogs/data/DC/database_content_updated-dc.json";

interface Blog {
  title: string;
  metaDescription: string;
  introParagraph: string;
  middleParagraph: string;
  conclusionParagraph: string;
  sk: string;
  subject?: string;
  city?: string;
  state?: string;
}

/**
 * Capitalizes dash-separated text into proper title case
 */
const capitalizeDashText = (text: string): string => {
  if (!text) return "";
  return text
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Filter and type the blog data
const typedBlogDataDC: Blog[] = (blogDataDC as any[])
  .filter((blog: any) => blog && blog.sk && blog.title)
  .map((blog: any) => ({
    ...blog,
    // Ensure sk doesn't start with a slash for consistent URL handling
    sk: blog.sk.startsWith("/") ? blog.sk.substring(1) : blog.sk,
  })) as Blog[];

/**
 * Template component for rendering individual blog pages
 */
const BlogPageTemplate: React.FC = () => {
  // Get SK and rest of path parameters
  const { sk, "*": restPath } = useParams<{ sk: string; "*": string }>();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [sk, restPath]);

  // Create the full path from the parameters
  const fullPath = restPath ? `${sk}/${restPath}` : sk;

  if (!fullPath) {
    return <Navigate to="/blog" replace />;
  }

  // Try to find the blog post by the full path or just the sk
  const blog = typedBlogDataDC.find((b) => b.sk === fullPath || b.sk === sk);

  // List of paths that shouldn't be treated as blogs
  const reservedPaths = [
    "about",
    "products",
    "locations",
    "contact",
    "login",
    "account",
    "signup",
    "register",
    "refund-policy",
    "forgot-password",
    "privacy-policy",
    "terms-of-service",
    "blog",
    "404",
    "favicon.ico",
    "manifest.json",
    "robots.txt",
    "sitemap.xml",
    "images",
    "js",
    "css",
    "fonts",
    "api",
  ];

  // Redirect if this is a reserved path
  if (
    !blog &&
    (reservedPaths.includes(sk) || reservedPaths.includes(fullPath))
  ) {
    console.log(`Path ${fullPath} is reserved and not a blog post`);
    return <Navigate to="/404" replace />;
  }

  // Redirect to 404 if no blog found
  if (!blog) {
    console.log(`No blog post found for path: ${fullPath}`);
    return <Navigate to="/404" replace />;
  }

  // Parse the blog slug to extract subject, city, and state
  const pathParts = blog.sk.split("/");
  let subjectPart, cityStatePart;

  if (pathParts.length >= 2) {
    [subjectPart, cityStatePart] = pathParts;
  } else {
    // Handle case where SK doesn't contain a slash
    subjectPart = blog.sk;
    cityStatePart = "";
  }

  let cityPart = "",
    statePart = "";
  if (cityStatePart) {
    const cityStateSegments = cityStatePart.split("-");
    if (cityStateSegments.length >= 2) {
      statePart = cityStateSegments.pop() || "";
      cityPart = cityStateSegments.join("-");
    } else {
      cityPart = cityStateSegments[0] || "";
    }
  }

  // Create the data item for the LocalSEO component
  const dataItem = {
    // Use blog properties if available, otherwise parse from URL
    subject: blog.subject || capitalizeDashText(subjectPart || "Cannabis"),
    city: blog.city || capitalizeDashText(cityPart || "Washington"),
    state: blog.state || (statePart || "DC").toUpperCase(),
  };

  return (
    <LocalSEO
      metaDescription={blog.metaDescription}
      title={blog.title}
      introParagraph={blog.introParagraph}
      middleParagraph={blog.middleParagraph}
      conclusionParagraph={blog.conclusionParagraph}
      dataItem={dataItem}
      sk={blog.sk}
    />
  );
};

export default BlogPageTemplate;
